import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { css, StyleSheet } from 'aphrodite';
import {
  faArrowLeft,
  faArrowRight,
  faBan,
  faCheck,
  faExternalLinkAlt,
  faImages,
  faPlus,
  faSearchPlus,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AsyncSelect from 'react-select/async';
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import {
  backgroundGrey,
  blackberry,
  coolGrey,
  grape,
  greyStroke,
  modernSalmon,
  modernSalmonBackground,
  positiveGreen,
  shadowLight,
  softAmber,
  softAmberBackground,
  softGreenDark,
  softPurple,
  softPurpleLight,
  white,
} from 'lib/css/colors';
import {
  BRAND_SKU_MAPPER_V2_FILTER_OPTIONS,
  BRAND_SKU_MAPPER_V2_PRODUCT_CANDIDATES,
  BRAND_SKU_MAPPER_V2_PRODUCTS,
  BRAND_SKU_MAPPER_V2_CANCEL_RUN,
  BRAND_SKU_MAPPER_V2_CREATE_MFR_PRODUCT,
  BRAND_SKU_MAPPER_V2_DELETE_CREATED_MFR_PRODUCT,
  BRAND_SKU_MAPPER_V2_REVIEW_CANDIDATE,
  BRAND_SKU_MAPPER_V2_START_RUN,
  getURL,
  getURLWithSearchParams,
} from 'lib/networking/endpoints';
import {
  BRAND_SKU_MAPPER_V2_POPULATION_TABS,
  BRAND_SKU_MAPPER_V2_REVIEW_STATUS_TABS,
  BrandSkuMapperV2Population,
  BrandSkuMapperV2ReviewAction,
  BrandSkuMapperV2ReviewStatus,
  BrandSkuMapperV2RunRequestField,
  BrandSkuMapperV2RunStatus,
  BrandSkuMapperV2Tier,
} from 'lib/enums/BrandSkuMapperV2Enums';
import { ProductType } from 'lib/enums/ProductType';
import { galleryStyles } from 'components/brand_sku_mapper_shared/galleryStyles';
import { countImages } from 'components/brand_sku_mapper_shared/imageHelpers';
import { ZoomImageGallery } from 'components/brand_sku_mapper_shared/ZoomImageGallery';
import { httpGet, httpPost } from 'lib/networking/http';
import { selectStyles } from 'lib/css/selectStyles';
import { formatRoundedCurrency } from 'lib/utils/FormatUtils';
import CancelRunButton from 'components/brand_sku_mapper_shared/CancelRunButton';
import CreateMfrProductsFromDistributorProductsModal from 'components/global_products/detail_view/CreateMfrProductsFromDistributorProductsModal';
import Container from 'components/shared/Container';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import useCancelRun from 'components/brand_sku_mapper_shared/useCancelRun';
import SwalDialog, { confirmRichAction, SwalToast } from 'lib/utils/toast';
import TabBar from 'components/shared/TabBar';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

// The candidate cards below the filter row use their own zIndex-stacked layers
// (see candCard/tierBadge/stickyHeader styles below); react-select's menu
// defaults to zIndex 1, so without this it renders underneath those cards.
const filterSelectStyles = {
  ...selectStyles,
  menu: base => ({ ...base, zIndex: 20 }),
};

const PAGE_SIZE = 20;
const MAX_CANDIDATES = 25;
const FILTER_OPTION_DEBOUNCE_MS = 400;
// Mirrors the backend's _MIN_FILTER_QUERY_LENGTH.
const MIN_FILTER_QUERY_LENGTH = 2;

// Type-to-search options for one dropdown field, cross-filtered by the others.
function fetchFilterOptions(field, query, crossFilters) {
  const params = { field, population: crossFilters.population };
  if (crossFilters.dpPartner != null) params.dp_partner = crossFilters.dpPartner;
  if (crossFilters.switchOn != null) params.switch = crossFilters.switchOn;
  const trimmed = (query || '').trim();
  if (trimmed.length >= MIN_FILTER_QUERY_LENGTH) params.query = trimmed;
  if (crossFilters.brandId) params.brand_id = crossFilters.brandId;
  if (crossFilters.distributorId) params.distributor_id = crossFilters.distributorId;
  if (crossFilters.manufacturerId) params.manufacturer_id = crossFilters.manufacturerId;
  return httpGet(getURLWithSearchParams(BRAND_SKU_MAPPER_V2_FILTER_OPTIONS, params)).then(res =>
    (res.data?.options || []).map(o => ({ value: o.id, label: `${o.name} (${o.count})` })),
  );
}

// Option labels are "Name (count)" (see fetchFilterOptions) -- strip the count
// for places that just want the plain name (the run confirmation dialog).
function optionName(option) {
  return option ? option.label.replace(/\s*\(\d+\)$/, '') : null;
}

// run.scope (see BrandSkuMappingRunV2Schema) -> "Brand Verified + brand X", or just
// the population label alone when nothing else narrows the run -- scope.population is
// always present now (see resolve_run_scope), so this never returns null anymore.
function scopeLabel(scope) {
  if (!scope) return null;
  const narrowParts = [
    scope.brand && `brand ${scope.brand}`,
    scope.distributor && `distributor ${scope.distributor}`,
    scope.manufacturer && `manufacturer ${scope.manufacturer}`,
  ].filter(Boolean);
  const parts = [scope.population, ...narrowParts].filter(Boolean);
  if (!parts.length) return null;
  return narrowParts.length ? `Scoped: ${parts.join(' + ')}` : parts[0];
}

// Debounce loadOptions so a fast typist doesn't fire one query per keystroke.
// A failed request surfaces a toast (distinct from a genuinely empty result)
// instead of silently resolving to []. Also settles/cancels any superseded or
// still-pending promise on the next keystroke or on unmount, so cancelled
// calls don't hang forever.
function useDebouncedLoadOptions(loader, label) {
  const timerRef = useRef(null);
  const pendingResolveRef = useRef(null);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      if (pendingResolveRef.current) pendingResolveRef.current([]);
    },
    [],
  );

  return useCallback(
    inputValue =>
      new Promise(resolve => {
        clearTimeout(timerRef.current);
        if (pendingResolveRef.current) pendingResolveRef.current([]);
        pendingResolveRef.current = resolve;
        timerRef.current = setTimeout(() => {
          pendingResolveRef.current = null;
          loader(inputValue)
            .then(resolve)
            .catch(() => {
              SwalToast('error', `Could not load ${label} options.`);
              resolve([]);
            });
        }, FILTER_OPTION_DEBOUNCE_MS);
      }),
    [loader, label],
  );
}

const CHANNEL_LABELS = {
  dense: 'Dense',
  sparse: 'Sparse',
  gtin: 'GTIN',
  gtin_ambiguous: 'GTIN (ambiguous)',
  sku_unique: 'MPN',
  sku_ambiguous: 'MPN (ambiguous)',
  brand_match: 'Brand',
};

const AMBIGUOUS_CHANNELS = new Set(['gtin_ambiguous', 'sku_ambiguous']);

// Keep in sync with BrandSkuMapperV2Service._POPULATION_LABELS.
const POPULATION_LABELS = {
  [BrandSkuMapperV2Population.GMV]: 'All GMV Products',
  [BrandSkuMapperV2Population.BRAND_VERIFIED]: 'Brand Verified',
  [BrandSkuMapperV2Population.BRAND_NOT_VERIFIED]: 'Brand Not Verified',
};

const POPULATION_PRODUCT_NOUN = {
  [BrandSkuMapperV2Population.GMV]: 'GMV product',
  [BrandSkuMapperV2Population.BRAND_VERIFIED]: 'brand-verified unmapped product',
  [BrandSkuMapperV2Population.BRAND_NOT_VERIFIED]: 'brand-not-verified unmapped product',
};

// Label/description for the population-specific switch: thumbnail presence
// for BRAND_VERIFIED, brand presence for BRAND_NOT_VERIFIED, and for GMV --
// Eligible (thumbnail AND verified brand, i.e. actually matchable today) vs
// Ineligible -- a real GMV product can still lack either.
const SWITCH_LABELS = {
  [BrandSkuMapperV2Population.GMV]: 'Eligible',
  [BrandSkuMapperV2Population.BRAND_VERIFIED]: 'Has Thumbnail',
  [BrandSkuMapperV2Population.BRAND_NOT_VERIFIED]: 'Has Brand',
};

function tierColor(tier) {
  if (tier === BrandSkuMapperV2Tier.HIGH) return positiveGreen;
  if (tier === BrandSkuMapperV2Tier.LOW) return softAmber;
  return coolGrey;
}

function pct(cal) {
  if (cal == null || Number.isNaN(Number(cal))) return '—';
  return `${(Number(cal) * 100).toFixed(1)}%`;
}

function ChannelBadges({ channels }) {
  if (!channels || channels.length === 0) return null;
  return (
    <div className={css(styles.channelBadgeRow)}>
      {channels.map(ch => (
        <span
          key={ch}
          className={css(
            styles.channelBadge,
            AMBIGUOUS_CHANNELS.has(ch) && styles.channelBadgeAmbiguous,
          )}
        >
          {CHANNEL_LABELS[ch] || ch}
        </span>
      ))}
    </div>
  );
}

ChannelBadges.propTypes = {
  channels: PropTypes.arrayOf(PropTypes.string),
};

// A hard binary switch (no "show all" state) -- shared by GMV's own DP-partner
// checkbox and the two unmapped populations' switch + DP-partner toggles.
function Toggle({ checked, onToggle, label, className, toggleRef }) {
  return (
    <div
      ref={toggleRef}
      role="switch"
      aria-checked={checked}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      className={`${css(styles.toggleWrapper)}${className ? ` ${className}` : ''}`}
    >
      <div className={css(styles.toggleTrack, checked && styles.toggleTrackOn)}>
        <div className={css(styles.toggleThumb, checked && styles.toggleThumbOn)} />
      </div>
      <span className={css(styles.toggleText)}>{label}</span>
    </div>
  );
}

Toggle.propTypes = {
  checked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  toggleRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

Toggle.defaultProps = {
  className: '',
};

// Shared by ProductPanel and the candidate grid: falls back to the placeholder
// both when there's no URL at all AND when the URL fails to load (a broken URL
// used to just hide the <img>, collapsing the layout into an empty gap). Resets
// its own error state when src changes so a stale placeholder doesn't linger
// after navigating to a different product/candidate with a valid image.
export function ProductImage({ src, imgClassName, placeholderClassName }) {
  const [hasError, setHasError] = useState(false);
  useEffect(() => setHasError(false), [src]);

  if (!src || hasError) {
    return <div className={placeholderClassName} />;
  }
  return (
    <img
      src={src}
      alt=""
      className={imgClassName}
      onError={e => {
        e.target.onerror = null;
        setHasError(true);
      }}
    />
  );
}

ProductImage.propTypes = {
  src: PropTypes.string,
  imgClassName: PropTypes.string.isRequired,
  placeholderClassName: PropTypes.string.isRequired,
};

export function ProductPanel({ product, side, label, zoomOpen, onZoomOpenChange }) {
  if (!product) {
    return (
      <div className={css(styles.productCard)}>
        <div className={css(styles.sideLabel)}>{label}</div>
        <div className={css(styles.mutedText)}>No product.</div>
      </div>
    );
  }

  const pdpHref =
    product.global_product_id && product.id
      ? `/global-product-detail/${product.global_product_id}/${product.id}/${
          side === 'vendor' ? ProductType.DISTRIBUTOR : ProductType.MANUFACTURER
        }`
      : null;

  const detailsLine =
    side === 'vendor'
      ? `${product.vendor || '—'} · SKU ${product.vendor_sku || '—'}`
      : `MFR SKU ${product.manufacturer_sku || '—'}`;
  const imageCount = countImages(product.thumbnail_url, product.image_urls);
  const gmvLine = <strong>{`GMV ${formatRoundedCurrency(product.gmv) || '—'}`}</strong>;

  return (
    <div className={css(styles.productCard)}>
      <div className={css(styles.sideLabel)}>{label}</div>
      {side === 'vendor' && (
        <div className={css(styles.candTopRightRow)}>
          <span className={css(styles.candStatusPill)}>
            {`sts:${(product.archived ? 'archived' : product.status || 'null').toLowerCase()}`}
          </span>
        </div>
      )}
      <div className={css(styles.productImageWrap)}>
        <div className={css(galleryStyles.productImageInner)}>
          <ProductImage
            src={product.thumbnail_url}
            imgClassName={css(styles.productImageLarge)}
            placeholderClassName={css(styles.productImagePlaceholderLarge)}
          />
          {imageCount > 0 && (
            <div className={css(galleryStyles.imageCountBadge)} title={`${imageCount} image(s)`}>
              <FontAwesomeIcon icon={faImages} /> {imageCount}
            </div>
          )}
        </div>
        <div className={css(styles.candIconRail, styles.productIconRail)}>
          <button
            type="button"
            className={css(styles.candIconButton)}
            onClick={() => onZoomOpenChange(true)}
            aria-label="Zoom in on product"
            title="Zoom in"
          >
            <FontAwesomeIcon icon={faSearchPlus} />
          </button>
          {pdpHref ? (
            <a
              href={pdpHref}
              target="_blank"
              rel="noopener noreferrer"
              className={css(styles.candIconButton)}
              aria-label="Open product page"
              title="Open product page"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </a>
          ) : (
            <span
              className={css(styles.candIconButton, styles.candIconButtonDisabled)}
              title="Product page unavailable"
              role="button"
              tabIndex={0}
              aria-disabled="true"
              aria-label="Product page unavailable"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </span>
          )}
        </div>
      </div>
      <div className={css(styles.productName)}>{product.name}</div>
      {product.data_source && (
        <span className={css(styles.dataSourceLabel)} title={product.data_source}>
          {product.data_source}
        </span>
      )}
      <div className={css(styles.mutedText)}>
        {product.brand || '—'} by {product.manufacturer || '—'}
      </div>
      <div className={css(styles.mutedText)}>{product.pack_size || '—'}</div>
      <div className={css(styles.mutedText)}>{detailsLine}</div>
      {side === 'vendor' && (
        <div className={css(styles.mutedText)}>{`MFR SKU ${product.mpn || '—'}`}</div>
      )}
      {side === 'vendor' && <div className={css(styles.mutedText)}>{gmvLine}</div>}
      {zoomOpen && (
        <Modal
          show
          onHide={() => onZoomOpenChange(false)}
          centered
          dialogClassName={css(styles.zoomModalDialog)}
        >
          <Modal.Body className={css(styles.zoomModalBody)}>
            {pdpHref && (
              <a
                href={pdpHref}
                target="_blank"
                rel="noopener noreferrer"
                className={css(styles.zoomCloseButton, styles.zoomPdpButton)}
                aria-label="Open product page"
                title="Open product page"
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </a>
            )}
            <button
              type="button"
              className={css(styles.zoomCloseButton)}
              onClick={() => onZoomOpenChange(false)}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <ZoomImageGallery thumbnailUrl={product.thumbnail_url} imageUrls={product.image_urls} />
            <div className={css(styles.zoomDetails)}>
              <div className={css(styles.zoomName)}>{product.name}</div>
              {product.data_source && (
                <span
                  className={css(styles.dataSourceLabel, styles.zoomDataSourceLabel)}
                  title={product.data_source}
                >
                  {product.data_source}
                </span>
              )}
              <div className={css(styles.mutedText)}>
                {product.brand || '—'} by {product.manufacturer || '—'}
              </div>
              <div className={css(styles.mutedText)}>{product.pack_size || '—'}</div>
              <div className={css(styles.mutedText)}>{detailsLine}</div>
              {side === 'vendor' && (
                <div className={css(styles.mutedText)}>{`MFR SKU ${product.mpn || '—'}`}</div>
              )}
              {side === 'vendor' && <div className={css(styles.mutedText)}>{gmvLine}</div>}
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}

ProductPanel.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    global_product_id: PropTypes.number,
    name: PropTypes.string,
    thumbnail_url: PropTypes.string,
    image_urls: PropTypes.arrayOf(PropTypes.string),
    vendor: PropTypes.string,
    brand: PropTypes.string,
    manufacturer: PropTypes.string,
    pack_size: PropTypes.string,
    vendor_sku: PropTypes.string,
    manufacturer_sku: PropTypes.string,
    mpn: PropTypes.string,
    data_source: PropTypes.string,
    status: PropTypes.string,
    archived: PropTypes.bool,
    gmv: PropTypes.number,
  }),
  side: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  zoomOpen: PropTypes.bool.isRequired,
  onZoomOpenChange: PropTypes.func.isRequired,
};

function BrandSkuMapperV2ReviewView({ population: initialPopulation }) {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState(BrandSkuMapperV2ReviewStatus.PENDING);
  const [searchInput, setSearchInput] = useState('');
  const [query] = useDebounce(searchInput, 400);
  const [brandOption, setBrandOption] = useState(null);
  const [distributorOption, setDistributorOption] = useState(null);
  const [manufacturerOption, setManufacturerOption] = useState(null);
  const brandId = brandOption?.value || '';
  const distributorId = distributorOption?.value || '';
  const manufacturerId = manufacturerOption?.value || '';
  // GMV's own checkbox (unrelated to the two unmapped populations' dpPartner toggle below).
  const [dpPartnerOnly, setDpPartnerOnly] = useState(true);
  // GMV tab only: order by the nightly GMV cache; products without GMV there go last.
  const [gmvSort, setGmvSort] = useState(false);
  // The two unmapped populations' switches: switchOn means has-thumbnail for
  // BRAND_VERIFIED, has-brand for BRAND_NOT_VERIFIED (see SWITCH_LABELS); dpPartner
  // is a second, independent toggle. Both are hard binaries (no "show all" state).
  const [switchOn, setSwitchOn] = useState(true);
  const [dpPartner, setDpPartner] = useState(true);
  // A cross-route tab click remounts this component under a new `key` (see
  // AppContainer); history.push's state carries the clicked population through.
  const [population, setPopulation] = useState(
    history?.location?.state?.population || initialPopulation,
  );
  const isGmv = population === BrandSkuMapperV2Population.GMV;
  const [pageIdx, setPageIdx] = useState(0);
  const [selectedMfrId, setSelectedMfrId] = useState(null);
  const [zoomedMfrProductId, setZoomedMfrProductId] = useState(null);
  const [zoomedProductOpen, setZoomedProductOpen] = useState(false);
  const [reviewingAction, setReviewingAction] = useState(null);
  const [showCreateMfrProduct, setShowCreateMfrProduct] = useState(false);
  const [runLimit, setRunLimit] = useState('');
  const [jumpValue, setJumpValue] = useState('');
  // Remembers each tab's last-viewed product (as a global index) so switching
  // PENDING -> APPROVED -> PENDING resumes where the user left off instead of
  // always snapping back to product 1.
  const [tabPositions, setTabPositions] = useState({});
  const reviewedIdRef = useRef(null);
  // GMV Sort sits on the Next-product row, directly under the Eligible toggle. The two rows
  // use different layouts (filter grid vs the max-width review area), so its x is measured.
  const eligibleToggleRef = useRef(null);
  const navRowRef = useRef(null);
  const [gmvSortLeft, setGmvSortLeft] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [query]);

  const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE, population };
  if (status) params.status = status;
  if (query) params.query = query;
  if (brandId) params.brand_id = brandId;
  if (distributorId) params.distributor_id = distributorId;
  if (manufacturerId) params.manufacturer_id = manufacturerId;
  params.switch = switchOn;
  if (isGmv) {
    if (dpPartnerOnly) params.dp_partner = true;
    if (gmvSort) params.gmv_sort = true;
  } else {
    params.dp_partner = dpPartner;
  }

  const { data, loading, refetch, silentRefetch } = useGet(BRAND_SKU_MAPPER_V2_PRODUCTS, params);

  // Same switch state every dropdown loader below cross-filters by -- memoized
  // so useCallback below only recomputes when a value actually changes, not
  // on every render (a plain object literal here would be a new reference
  // each render, defeating that memoization).
  const switchFilters = useMemo(
    () => ({ dpPartner: isGmv ? (dpPartnerOnly ? true : undefined) : dpPartner, switchOn }),
    [isGmv, dpPartnerOnly, dpPartner, switchOn],
  );
  // Forces the dropdown AsyncSelects below to re-query when any switch changes.
  const switchesKey = `${dpPartnerOnly}-${switchOn}-${dpPartner}-${population}`;

  // Dropdown options, cross-filtered by whichever of the other two is selected.
  const loadBrandOptions = useDebouncedLoadOptions(
    useCallback(
      q =>
        fetchFilterOptions('brand', q, {
          distributorId,
          manufacturerId,
          population,
          ...switchFilters,
        }),
      [distributorId, manufacturerId, population, switchFilters],
    ),
    'brand',
  );
  const loadDistributorOptions = useDebouncedLoadOptions(
    useCallback(
      q =>
        fetchFilterOptions('distributor', q, {
          brandId,
          manufacturerId,
          population,
          ...switchFilters,
        }),
      [brandId, manufacturerId, population, switchFilters],
    ),
    'distributor',
  );
  const loadManufacturerOptions = useDebouncedLoadOptions(
    useCallback(
      q =>
        fetchFilterOptions('manufacturer', q, {
          brandId,
          distributorId,
          population,
          ...switchFilters,
        }),
      [brandId, distributorId, population, switchFilters],
    ),
    'manufacturer',
  );

  const latestRun = data?.latest_run || null;
  const candidates = useMemo(() => data?.candidates || [], [data]);
  const total = data?.count || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const countsByStatus = data?.counts_by_status || {};

  const runIsRunning = latestRun?.status === BrandSkuMapperV2RunStatus.RUNNING;
  const runIsPending = latestRun?.status === BrandSkuMapperV2RunStatus.PENDING;
  const processedCount = latestRun?.processed_count || 0;
  const totalCount = latestRun?.total_vendor_products || 0;

  // Poll while a run is active so status/counts refresh without manual reload.
  useEffect(() => {
    if (!runIsRunning && !runIsPending) return undefined;
    const id = setInterval(silentRefetch, 4000);
    return () => clearInterval(id);
  }, [runIsRunning, runIsPending, silentRefetch]);

  // status is deliberately excluded: the TabBar onSelect handler below owns
  // pageIdx on a tab switch (restoring the remembered position instead of
  // always resetting to 0) -- this staying in the deps would stomp that
  // restore back to 0 on every switch.
  useEffect(() => {
    setPageIdx(0);
  }, [
    query,
    brandId,
    distributorId,
    manufacturerId,
    dpPartnerOnly,
    gmvSort,
    switchOn,
    dpPartner,
    population,
  ]);

  const safeIdx = Math.min(pageIdx, Math.max(candidates.length - 1, 0));
  const candidate = candidates[safeIdx];
  const hasCandidate = !!candidate;

  useLayoutEffect(() => {
    const measure = () => {
      if (!eligibleToggleRef.current || !navRowRef.current) {
        setGmvSortLeft(null);
        return;
      }
      setGmvSortLeft(
        eligibleToggleRef.current.getBoundingClientRect().left -
          navRowRef.current.getBoundingClientRect().left,
      );
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [loading, hasCandidate]);
  const globalIdx = page * PAGE_SIZE + safeIdx;
  const vpId = candidate?.vendor_product?.id || null;

  // Reset selection when the DP changes, seeding it from the already-approved
  // pick (if any) so an already-reviewed product shows what it matched to
  // instead of looking unselected. Also closes both zoom modals so neither
  // one can keep showing a product that's no longer the one on screen.
  useEffect(() => {
    setSelectedMfrId(candidate?.picked_mfr_product_id ?? null);
    setZoomedMfrProductId(null);
    setZoomedProductOpen(false);
  }, [candidate?.id, candidate?.picked_mfr_product_id]);

  // Fetch the DP's ranked top-25 candidates from the S3 results file.
  const candUrl = vpId
    ? getURL(BRAND_SKU_MAPPER_V2_PRODUCT_CANDIDATES, { global_product_vendor_id: vpId })
    : BRAND_SKU_MAPPER_V2_PRODUCT_CANDIDATES;
  const { data: candData, loading: candLoading } = useGet(candUrl, {}, !vpId);
  const pickedMfrProduct = candidate?.picked_mfr_product;
  const allRankedCandidates = useMemo(() => {
    const ranked = (candData?.candidates || []).slice(0, MAX_CANDIDATES);
    // A picked product outside the top-25 (e.g. a newly created one) goes first, unranked.
    if (!pickedMfrProduct || ranked.some(c => c.mfr_product_id === pickedMfrProduct.id)) {
      return ranked;
    }
    return [{ mfr_product_id: pickedMfrProduct.id, mfr_product: pickedMfrProduct }, ...ranked];
  }, [candData, pickedMfrProduct]);
  // Derived (by id) rather than a captured object, so a refetch of
  // allRankedCandidates while the modal is open can never leave it showing stale data.
  const zoomedCandidate =
    zoomedMfrProductId != null
      ? allRankedCandidates.find(c => c.mfr_product_id === zoomedMfrProductId) || null
      : null;

  // If the zoomed candidate drops out of the list (client-side filter, refetch),
  // clear the id rather than leaving it set for a modal that's already unmounted.
  useEffect(() => {
    if (zoomedMfrProductId != null && !zoomedCandidate) {
      setZoomedMfrProductId(null);
    }
  }, [zoomedMfrProductId, zoomedCandidate]);

  const nCands = allRankedCandidates.length;
  const candRangeLabel = `${nCands} candidate${nCands === 1 ? '' : 's'}`;

  const atFirst = page === 0 && safeIdx === 0;
  const atLast = page >= totalPages - 1 && safeIdx >= candidates.length - 1;

  const goPrevProduct = useCallback(() => {
    if (safeIdx > 0) setPageIdx(safeIdx - 1);
    else if (page > 0) {
      setPage(page - 1);
      setPageIdx(PAGE_SIZE - 1);
    }
  }, [page, safeIdx]);

  const goNextProduct = useCallback(() => {
    if (safeIdx < candidates.length - 1) setPageIdx(safeIdx + 1);
    else if (page < totalPages - 1) {
      setPage(page + 1);
      setPageIdx(0);
    }
  }, [candidates.length, page, safeIdx, totalPages]);

  // Jumps straight to the Nth product (0-indexed) instead of stepping one at a
  // time -- used by both the manual "jump to product" input and by restoring
  // a tab's remembered position.
  const goToGlobalIndex = useCallback(idx => {
    const clamped = Math.max(0, idx);
    setPage(Math.floor(clamped / PAGE_SIZE));
    setPageIdx(clamped % PAGE_SIZE);
  }, []);

  // Keep the jump-to-product input in sync with real navigation (prev/next,
  // tab restore) -- only fires when globalIdx itself changes, so it never
  // clobbers what the user is mid-typing.
  useEffect(() => {
    setJumpValue(String(globalIdx + 1));
  }, [globalIdx]);

  const jumpToProduct = useCallback(() => {
    const n = Number(jumpValue);
    if (Number.isInteger(n) && n >= 1 && n <= total) {
      goToGlobalIndex(n - 1);
    } else {
      setJumpValue(String(globalIdx + 1));
    }
  }, [jumpValue, total, globalIdx, goToGlobalIndex]);

  const review = useCallback(
    (action, pickedMfrProductId = null, overrideManufacturerMismatch = false) => {
      if (!candidate || reviewingAction !== null) return;
      setReviewingAction(action);
      httpPost(getURL(BRAND_SKU_MAPPER_V2_REVIEW_CANDIDATE, { candidate_id: candidate.id }), {
        action,
        picked_mfr_product_id: pickedMfrProductId,
        override_manufacturer_mismatch: overrideManufacturerMismatch,
      })
        .then(() => {
          // If the reviewed item stays in the filtered list after refetch (e.g.
          // the "All" tab), advance past it; if it leaves the list (e.g. the
          // Pending tab), the list shifts up by itself — advancing would skip one.
          if (!atLast) reviewedIdRef.current = candidate.id;
          refetch();
        })
        .catch(err => {
          const body = err?.response?.data;
          if (body?.code === 'manufacturer_mismatch') {
            setReviewingAction(null);
            if (body.overridable && !overrideManufacturerMismatch) {
              confirmRichAction(
                () => review(action, pickedMfrProductId, true),
                body.reason || body.message,
                'Approve anyway?',
                'Approve anyway',
              );
            } else {
              SwalDialog('error', body.reason || body.message, 'Cannot approve');
            }
            return;
          }
          SwalDialog('error', body?.message || 'Failed to update candidate.', 'Error');
        })
        .finally(() => setReviewingAction(null));
    },
    [candidate, reviewingAction, atLast, refetch],
  );

  // Memoized so the shared modal's manufacturer select keeps the reviewer's pick across renders.
  const createMfrProductDps = useMemo(
    () =>
      candidate?.vendor_product
        ? [
            {
              ...candidate.vendor_product,
              brand: { global_manufacturer: candidate.vendor_product.brand_manufacturer },
            },
          ]
        : [],
    [candidate?.vendor_product],
  );
  const onMfrProductCreated = useCallback(() => {
    if (!atLast) reviewedIdRef.current = candidate?.id;
    refetch();
  }, [atLast, candidate, refetch]);

  const hasCreatedMfrProduct = !!candidate?.has_created_mfr_product;
  const deleteCreatedMfrProduct = useCallback(() => {
    if (!candidate || reviewingAction !== null) return;
    confirmRichAction(
      () => {
        setReviewingAction('delete');
        httpPost(
          getURL(BRAND_SKU_MAPPER_V2_DELETE_CREATED_MFR_PRODUCT, { candidate_id: candidate.id }),
          {},
        )
          .then(refetch)
          .catch(err =>
            SwalDialog('error', err?.response?.data?.message || 'Delete failed.', 'Error'),
          )
          .finally(() => setReviewingAction(null));
      },
      `Permanently deletes ${candidate.picked_mfr_product?.name}; the row goes back to Pending.`,
      'Delete created manufacturer product?',
      'Delete',
    );
  }, [candidate, reviewingAction, refetch]);

  // After a review + refetch: advance only if the reviewed item is still in the
  // filtered list (mirrors V1's reviewedIdRef pattern — see review() above).
  useEffect(() => {
    if (!reviewedIdRef.current) return;
    const stillPresent = candidates.some(c => c.id === reviewedIdRef.current);
    reviewedIdRef.current = null;
    if (stillPresent) {
      goNextProduct();
    }
  }, [candidates, goNextProduct]);

  // Keyboard shortcuts: ←/→ move between DP products; A approves the SELECTED
  // card (V2 never auto-approves rank-1); R rejects; N marks no-match.
  useEffect(() => {
    const isTyping = () => {
      const tag = document.activeElement?.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable;
    };
    const handler = e => {
      if (
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        isTyping() ||
        reviewingAction !== null ||
        zoomedMfrProductId != null ||
        zoomedProductOpen ||
        showCreateMfrProduct
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrevProduct();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNextProduct();
      } else if (!candidate) {
        return;
      } else if (e.key === 'a' || e.key === 'A') {
        if (selectedMfrId && candidate.review_status !== BrandSkuMapperV2ReviewStatus.APPROVED) {
          e.preventDefault();
          review(BrandSkuMapperV2ReviewAction.APPROVE, selectedMfrId);
        }
      } else if (e.key === 'r' || e.key === 'R') {
        if (
          candidate.review_status !== BrandSkuMapperV2ReviewStatus.REJECTED &&
          !candidate.has_created_mfr_product
        ) {
          e.preventDefault();
          review(BrandSkuMapperV2ReviewAction.REJECT);
        }
      } else if (e.key === 'n' || e.key === 'N') {
        if (
          candidate.review_status !== BrandSkuMapperV2ReviewStatus.NO_MATCH &&
          !candidate.has_created_mfr_product
        ) {
          e.preventDefault();
          review(BrandSkuMapperV2ReviewAction.NO_MATCH);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    candidate,
    goNextProduct,
    goPrevProduct,
    review,
    reviewingAction,
    selectedMfrId,
    showCreateMfrProduct,
    zoomedMfrProductId,
    zoomedProductOpen,
  ]);

  const { postData: dispatchRun, loading: dispatchingRun } = usePost(
    BRAND_SKU_MAPPER_V2_START_RUN,
    () => {
      SwalToast('success', 'Run started.');
      refetch();
    },
    err => {
      const msg = err?.response?.data?.message || 'Could not start a run.';
      SwalToast('error', msg);
    },
  );

  const { cancelRun, cancelling: cancellingRun } = useCancelRun(
    BRAND_SKU_MAPPER_V2_CANCEL_RUN,
    () => refetch(),
  );

  const startRun = useCallback(
    remainingOnly => {
      const parsedLimit = Number(runLimit);
      const hasLimit = runLimit !== '' && Number.isInteger(parsedLimit) && parsedLimit > 0;
      // GMV's switch flips the run between the eligible and ineligible pool.
      const poolAdjective = isGmv && !switchOn ? 'ineligible' : 'eligible';
      const productNoun = POPULATION_PRODUCT_NOUN[population];

      // A scoped run must never read like a full-catalog run -- this dialog is
      // the only point where the user confirms what's about to be dispatched.
      const scopeParts = [
        brandOption && `brand "${optionName(brandOption)}"`,
        distributorOption && `distributor "${optionName(distributorOption)}"`,
        manufacturerOption && `manufacturer "${optionName(manufacturerOption)}"`,
      ].filter(Boolean);
      const scopeBanner = scopeParts.length
        ? `<div class="${css(styles.scopeWarning)}"><strong>Scoped to ${scopeParts.join(
            ' + ',
          )}.</strong> ` + `Products outside this scope will not be touched.</div>`
        : '';
      const eligibleText = scopeParts.length
        ? `${poolAdjective} product in this scope`
        : `${poolAdjective} ${productNoun}`;

      const confirmTitle = remainingOnly ? 'Run for new products only?' : 'Run for all products?';
      const confirmHtml =
        scopeBanner +
        (hasLimit
          ? `Score up to <strong>${parsedLimit.toLocaleString()}</strong> of the ` +
            `${poolAdjective} products.`
          : remainingOnly
          ? `Only ${productNoun}s that have never been scored before will be processed.`
          : `Re-score <strong>every</strong> ${eligibleText}, including ` +
            `ones already matched or reviewed. Depending on catalog size, this can take a while.`);

      confirmRichAction(
        () =>
          dispatchRun({
            [BrandSkuMapperV2RunRequestField.REMAINING_ONLY]: remainingOnly,
            [BrandSkuMapperV2RunRequestField.POPULATION]: population,
            ...(hasLimit ? { [BrandSkuMapperV2RunRequestField.MAX_PRODUCTS]: parsedLimit } : {}),
            ...(brandId ? { [BrandSkuMapperV2RunRequestField.BRAND_ID]: brandId } : {}),
            ...(distributorId
              ? { [BrandSkuMapperV2RunRequestField.DISTRIBUTOR_ID]: distributorId }
              : {}),
            ...(manufacturerId
              ? { [BrandSkuMapperV2RunRequestField.MANUFACTURER_ID]: manufacturerId }
              : {}),
            // The Eligible/Ineligible switch scopes the run for every population,
            // GMV included. The DP-partner checkbox stays browse-only for GMV --
            // a GMV run scores the full pool across partners and non-partners.
            [BrandSkuMapperV2RunRequestField.SWITCH]: switchOn,
            ...(isGmv ? {} : { [BrandSkuMapperV2RunRequestField.DP_PARTNER]: dpPartner }),
          }),
        confirmHtml,
        confirmTitle,
        remainingOnly ? 'Yes, run for new only' : 'Yes, run for all',
      );
    },
    [
      dispatchRun,
      runLimit,
      brandId,
      distributorId,
      manufacturerId,
      brandOption,
      distributorOption,
      manufacturerOption,
      population,
      isGmv,
      switchOn,
      dpPartner,
    ],
  );

  return (
    <>
      <Container className={css(styles.container)}>
        <div className={css(styles.headerRow)}>
          <div className={css(styles.headerLeft)}>
            <span className={css(styles.title)}>
              {`Brand SKU Mapper V2 — ${POPULATION_LABELS[population]}`}
            </span>
            {latestRun && !runIsRunning && !runIsPending && (
              <span className={css(styles.headerMeta)}>
                {`${latestRun.matched_count || 0}/${latestRun.total_vendor_products || 0} matched`}
              </span>
            )}
            {latestRun?.scope && !runIsRunning && !runIsPending && (
              <span className={css(styles.scopeBadge)}>{scopeLabel(latestRun.scope)}</span>
            )}
            {(runIsRunning || runIsPending) && (
              <span className={css(styles.runningIndicator)}>
                <Spinner animation="border" size="sm" className="mr-1" />
                {runIsPending ? 'Queued…' : `Scoring ${processedCount}/${totalCount}…`}
                <CancelRunButton
                  cancelling={cancellingRun}
                  onClick={() => cancelRun({ population })}
                />
              </span>
            )}
            {latestRun?.scope && (runIsRunning || runIsPending) && (
              <span className={css(styles.scopeBadge)}>{scopeLabel(latestRun.scope)}</span>
            )}
            {runIsRunning && totalCount > 0 && (
              <div className={css(styles.progressTrack)}>
                <div
                  className={css(styles.progressFill)}
                  style={{ width: `${Math.min(100, (100 * processedCount) / totalCount)}%` }}
                />
              </div>
            )}
            {!runIsRunning && !runIsPending && (
              <>
                <Form.Control
                  type="number"
                  min="1"
                  placeholder="Limit"
                  value={runLimit}
                  onChange={e => setRunLimit(e.target.value)}
                  className={css(styles.limitInput)}
                  title="Optional: cap how many products this run scores, filtered or not"
                />
                <Button
                  size="sm"
                  className={css(styles.dispatchBtn)}
                  onClick={() => startRun(false)}
                  disabled={dispatchingRun}
                  title={`Score every ${POPULATION_PRODUCT_NOUN[population]}`}
                >
                  Run for All
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className={css(styles.newRunBtn)}
                  onClick={() => startRun(true)}
                  disabled={dispatchingRun}
                  title="Score only products not scored before"
                >
                  Run for New Only
                </Button>
              </>
            )}
          </div>
          <Badge className={css(styles.totalBadge)}>{total.toLocaleString()} products</Badge>
        </div>

        <TabBar
          tabs={BRAND_SKU_MAPPER_V2_POPULATION_TABS}
          activeKey={population}
          onSelect={key => {
            if (key === population) return;
            setPage(0);
            if (key === BrandSkuMapperV2Population.GMV) {
              history.push('/enrich-products/brand-sku-mapper-v2', { population: key });
            } else if (isGmv) {
              history.push('/enrich-products/brand-sku-mapper-v2/unmapped', { population: key });
            } else {
              // No remount here (same route) -- clear scope filters explicitly
              // so a stale selection doesn't silently empty the new population.
              setBrandOption(null);
              setDistributorOption(null);
              setManufacturerOption(null);
              setPopulation(key);
            }
          }}
        />

        <TabBar
          tabs={BRAND_SKU_MAPPER_V2_REVIEW_STATUS_TABS.map(t => ({
            key: t.key,
            label: t.label,
            count: t.key && countsByStatus[t.key] != null ? countsByStatus[t.key] : undefined,
          }))}
          activeKey={status}
          onSelect={key => {
            if (key === status) return;
            setTabPositions(prev => ({ ...prev, [status]: globalIdx }));
            const saved = tabPositions[key] ?? 0;
            const cap = countsByStatus[key] != null ? Math.max(countsByStatus[key] - 1, 0) : saved;
            setStatus(key);
            goToGlobalIndex(Math.min(saved, cap));
          }}
        />

        <Row className="mt-3 align-items-center flex-wrap g-2">
          <Col md={3}>
            <Form.Control
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search vendor product name or SKU"
            />
          </Col>
          <Col md={2}>
            <AsyncSelect
              key={`brand-${distributorId}-${manufacturerId}-${switchesKey}`}
              isClearable
              defaultOptions
              loadOptions={loadBrandOptions}
              value={brandOption}
              onChange={opt => {
                setBrandOption(opt || null);
                setPage(0);
              }}
              placeholder="Filter by brand"
              noOptionsMessage={() => 'No matching brands'}
              styles={filterSelectStyles}
            />
          </Col>
          <Col md={2}>
            <AsyncSelect
              key={`distributor-${brandId}-${manufacturerId}-${switchesKey}`}
              isClearable
              defaultOptions
              loadOptions={loadDistributorOptions}
              value={distributorOption}
              onChange={opt => {
                setDistributorOption(opt || null);
                setPage(0);
              }}
              placeholder="Filter by distributor"
              noOptionsMessage={() => 'No matching distributors'}
              styles={filterSelectStyles}
            />
          </Col>
          <Col md={2}>
            <AsyncSelect
              key={`manufacturer-${brandId}-${distributorId}-${switchesKey}`}
              isClearable
              defaultOptions
              loadOptions={loadManufacturerOptions}
              value={manufacturerOption}
              onChange={opt => {
                setManufacturerOption(opt || null);
                setPage(0);
              }}
              placeholder="Filter by manufacturer"
              noOptionsMessage={() => 'No matching manufacturers'}
              styles={filterSelectStyles}
            />
          </Col>
          <Col md="auto" className="d-flex align-items-center">
            <Toggle
              checked={switchOn}
              onToggle={() => {
                setSwitchOn(p => !p);
                setPage(0);
              }}
              label={SWITCH_LABELS[population]}
              className="mr-3"
              toggleRef={eligibleToggleRef}
            />
            <Toggle
              checked={isGmv ? dpPartnerOnly : dpPartner}
              onToggle={() => {
                if (isGmv) {
                  setDpPartnerOnly(p => !p);
                } else {
                  setDpPartner(p => !p);
                }
                setPage(0);
              }}
              label="DP Partners Only"
            />
          </Col>
        </Row>

        {loading ? (
          <LoadingSpinner />
        ) : !candidate ? (
          <div className="text-center text-muted p-5">No products to review.</div>
        ) : (
          <div className={css(styles.reviewWrapper)}>
            <div ref={navRowRef} className={css(styles.navRow)}>
              {isGmv && gmvSortLeft != null && (
                <div className={css(styles.gmvSortSlot)} style={{ left: gmvSortLeft }}>
                  <Toggle
                    checked={gmvSort}
                    onToggle={() => {
                      setGmvSort(p => !p);
                      setPage(0);
                    }}
                    label="GMV Sort"
                  />
                </div>
              )}
              <Button
                onClick={goPrevProduct}
                disabled={atFirst || reviewingAction !== null}
                className={css(styles.navBtn)}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Prev product
              </Button>
              <div className={css(styles.positionText)}>
                {total > 0 ? (
                  <>
                    <Form.Control
                      type="number"
                      min="1"
                      max={total}
                      value={jumpValue}
                      disabled={reviewingAction !== null}
                      onChange={e => setJumpValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }}
                      onBlur={jumpToProduct}
                      className={css(styles.jumpInput)}
                      title="Jump to product number"
                    />
                    {` of ${total.toLocaleString()}`}
                  </>
                ) : (
                  '—'
                )}
              </div>
              <Button
                onClick={goNextProduct}
                disabled={atLast || reviewingAction !== null}
                className={css(styles.navBtn)}
              >
                Next product
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </Button>
            </div>

            {candidate.review_status &&
              candidate.review_status !== BrandSkuMapperV2ReviewStatus.PENDING && (
                <div className={css(styles.statusBanner)}>
                  {candidate.review_status}
                  {candidate.picked_mfr_product
                    ? ` · matched to ${candidate.picked_mfr_product.name}`
                    : ''}
                  {candidate.reviewed_by ? ` · by ${candidate.reviewed_by}` : ''}
                  {candidate.review_note ? ` · ${candidate.review_note}` : ''}
                </div>
              )}

            <Row className={css(styles.actionsRow)}>
              <Col xs={3}>
                <Button
                  block
                  className={css(styles.approveBtn)}
                  onClick={() => review(BrandSkuMapperV2ReviewAction.APPROVE, selectedMfrId)}
                  disabled={
                    reviewingAction !== null ||
                    !selectedMfrId ||
                    candidate.review_status === BrandSkuMapperV2ReviewStatus.APPROVED
                  }
                >
                  {reviewingAction === BrandSkuMapperV2ReviewAction.APPROVE ? (
                    <Spinner animation="border" size="sm" className="mr-2" />
                  ) : (
                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                  )}
                  Approve selected match
                </Button>
              </Col>
              <Col xs={3}>
                <Button
                  block
                  className={css(styles.neutralBtn)}
                  onClick={() => review(BrandSkuMapperV2ReviewAction.NO_MATCH)}
                  disabled={
                    reviewingAction !== null ||
                    hasCreatedMfrProduct ||
                    candidate.review_status === BrandSkuMapperV2ReviewStatus.NO_MATCH
                  }
                >
                  {reviewingAction === BrandSkuMapperV2ReviewAction.NO_MATCH ? (
                    <Spinner animation="border" size="sm" className="mr-2" />
                  ) : (
                    <FontAwesomeIcon icon={faBan} className="mr-2" />
                  )}
                  No match exists
                </Button>
              </Col>
              <Col xs={3}>
                <Button
                  block
                  className={css(styles.rejectBtn)}
                  onClick={() => review(BrandSkuMapperV2ReviewAction.REJECT)}
                  disabled={
                    reviewingAction !== null ||
                    hasCreatedMfrProduct ||
                    candidate.review_status === BrandSkuMapperV2ReviewStatus.REJECTED
                  }
                >
                  {reviewingAction === BrandSkuMapperV2ReviewAction.REJECT ? (
                    <Spinner animation="border" size="sm" className="mr-2" />
                  ) : (
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  )}
                  Reject
                </Button>
              </Col>
              <Col xs={3}>
                {hasCreatedMfrProduct ? (
                  <Button
                    block
                    className={css(styles.rejectBtn)}
                    onClick={deleteCreatedMfrProduct}
                    disabled={reviewingAction !== null}
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-2" />
                    Delete created MFR product
                  </Button>
                ) : (
                  <Button
                    block
                    className={css(styles.neutralBtn)}
                    onClick={() => setShowCreateMfrProduct(true)}
                    disabled={
                      reviewingAction !== null ||
                      candidate.review_status === BrandSkuMapperV2ReviewStatus.APPROVED
                    }
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Create new MFR product
                  </Button>
                )}
              </Col>
            </Row>
            {showCreateMfrProduct && (
              <CreateMfrProductsFromDistributorProductsModal
                selectedProductsList={createMfrProductDps}
                url={getURL(BRAND_SKU_MAPPER_V2_CREATE_MFR_PRODUCT, { candidate_id: candidate.id })}
                onHide={() => setShowCreateMfrProduct(false)}
                refetch={onMfrProductCreated}
              />
            )}
            {!selectedMfrId && (
              <div className={css(styles.hintText)}>
                Select a candidate card below to enable “Approve”. Rank-1 is a suggestion — nothing
                is mapped until you pick and approve.
              </div>
            )}

            <Row className="mt-3">
              <Col md={4}>
                <ProductPanel
                  product={candidate.vendor_product}
                  side="vendor"
                  label="Distributor product to map"
                  zoomOpen={zoomedProductOpen}
                  onZoomOpenChange={setZoomedProductOpen}
                />
              </Col>
              <Col md={8}>
                <div className={css(styles.candHeader)}>
                  <span className={css(styles.candHeaderText)}>{candRangeLabel}</span>
                </div>

                {candLoading ? (
                  <LoadingSpinner />
                ) : allRankedCandidates.length === 0 ? (
                  <div className={css(styles.mutedText) + ' p-4 text-center'}>
                    No candidates in the results file for this product.
                  </div>
                ) : (
                  <div className={css(styles.candScrollArea)}>
                    <div className={css(styles.candGrid)}>
                      {allRankedCandidates.map(c => {
                        const isSelected = selectedMfrId === c.mfr_product_id;
                        const isMatched = candidate.picked_mfr_product_id === c.mfr_product_id;
                        const mp = c.mfr_product || {};
                        return (
                          <div
                            key={c.mfr_product_id}
                            className={css(
                              styles.candCard,
                              isSelected && styles.candCardSelected,
                              isMatched && styles.candCardMatched,
                            )}
                            onClick={() => setSelectedMfrId(isSelected ? null : c.mfr_product_id)}
                            onKeyDown={e => {
                              // icon rail handles its own keys
                              if (e.target !== e.currentTarget) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedMfrId(isSelected ? null : c.mfr_product_id);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {c.rank != null && (
                              <div className={css(styles.rankBadge)}>#{c.rank}</div>
                            )}
                            {isMatched && <div className={css(styles.matchedBadge)}>✓ Matched</div>}
                            <div className={css(styles.candTopRightRow)}>
                              {mp.data_source && (
                                <span
                                  className={css(styles.dataSourceLabel)}
                                  title={mp.data_source}
                                >
                                  {mp.data_source}
                                </span>
                              )}
                              <span
                                className={css(styles.tierPillFloating)}
                                style={{ backgroundColor: tierColor(c.tier) }}
                              >
                                {pct(c.calibrated_confidence)} · {c.tier || '—'}
                              </span>
                            </div>
                            <div className={css(styles.candImageWrap)}>
                              <div className={css(galleryStyles.candImageInner)}>
                                <ProductImage
                                  src={mp.thumbnail_url}
                                  imgClassName={css(styles.candImage)}
                                  placeholderClassName={css(styles.candImagePlaceholder)}
                                />
                                {countImages(mp.thumbnail_url, mp.image_urls) > 0 && (
                                  <div
                                    className={css(galleryStyles.imageCountBadge)}
                                    title={`${countImages(
                                      mp.thumbnail_url,
                                      mp.image_urls,
                                    )} image(s)`}
                                  >
                                    <FontAwesomeIcon icon={faImages} />{' '}
                                    {countImages(mp.thumbnail_url, mp.image_urls)}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={css(styles.candName)}>
                              {mp.name || `MFR #${c.mfr_product_id}`}
                            </div>
                            <div className={css(styles.candIconRail)}>
                              <button
                                type="button"
                                className={css(styles.candIconButton)}
                                onClick={e => {
                                  e.stopPropagation();
                                  setZoomedMfrProductId(c.mfr_product_id);
                                }}
                                aria-label="Zoom in on candidate"
                                title="Zoom in"
                              >
                                <FontAwesomeIcon icon={faSearchPlus} />
                              </button>
                              {mp.global_product_id && mp.id ? (
                                <a
                                  href={`/global-product-detail/${mp.global_product_id}/${mp.id}/${ProductType.MANUFACTURER}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={css(styles.candIconButton)}
                                  onClick={e => e.stopPropagation()}
                                  aria-label="Open manufacturer product page"
                                  title="Open product page"
                                >
                                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                                </a>
                              ) : (
                                <span
                                  className={css(
                                    styles.candIconButton,
                                    styles.candIconButtonDisabled,
                                  )}
                                  title="Product page unavailable"
                                  role="button"
                                  tabIndex={0}
                                  aria-disabled="true"
                                  aria-label="Product page unavailable"
                                >
                                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                                </span>
                              )}
                            </div>
                            <div className={css(styles.candBottomBlock)}>
                              <div className={css(styles.mutedText)}>
                                {mp.brand || '—'} by {mp.manufacturer || '—'}
                              </div>
                              <div className={css(styles.mutedText)}>{mp.pack_size || '—'}</div>
                              <div className={css(styles.candSkuReasonRow)}>
                                <span className={css(styles.mutedText)}>
                                  {`MFR SKU ${mp.manufacturer_sku || '—'}`}
                                </span>
                                <ChannelBadges channels={c.channels} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        )}
      </Container>
      {zoomedCandidate && (
        <Modal
          show
          onHide={() => setZoomedMfrProductId(null)}
          centered
          dialogClassName={css(styles.zoomModalDialog)}
        >
          <Modal.Body className={css(styles.zoomModalBody)}>
            {(zoomedCandidate.mfr_product || {}).global_product_id &&
              (zoomedCandidate.mfr_product || {}).id && (
                <a
                  href={`/global-product-detail/${zoomedCandidate.mfr_product.global_product_id}/${zoomedCandidate.mfr_product.id}/${ProductType.MANUFACTURER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css(styles.zoomCloseButton, styles.zoomPdpButton)}
                  aria-label="Open product page"
                  title="Open product page"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </a>
              )}
            <button
              type="button"
              className={css(styles.zoomCloseButton)}
              onClick={() => setZoomedMfrProductId(null)}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <ZoomImageGallery
              thumbnailUrl={(zoomedCandidate.mfr_product || {}).thumbnail_url}
              imageUrls={(zoomedCandidate.mfr_product || {}).image_urls}
            />
            <div className={css(styles.zoomDetails)}>
              <div className={css(styles.candName, styles.zoomName)}>
                {(zoomedCandidate.mfr_product || {}).name ||
                  `MFR #${zoomedCandidate.mfr_product_id}`}
              </div>
              {(zoomedCandidate.mfr_product || {}).data_source && (
                <span
                  className={css(styles.dataSourceLabel, styles.zoomDataSourceLabel)}
                  title={(zoomedCandidate.mfr_product || {}).data_source}
                >
                  {(zoomedCandidate.mfr_product || {}).data_source}
                </span>
              )}
              <span
                className={css(styles.tierPillFloating, styles.zoomTierPill)}
                style={{ backgroundColor: tierColor(zoomedCandidate.tier) }}
              >
                {pct(zoomedCandidate.calibrated_confidence)} · {zoomedCandidate.tier || '—'}
              </span>
              <div className={css(styles.mutedText)}>
                {(zoomedCandidate.mfr_product || {}).brand || '—'} by{' '}
                {(zoomedCandidate.mfr_product || {}).manufacturer || '—'}
              </div>
              <div className={css(styles.mutedText)}>
                {(zoomedCandidate.mfr_product || {}).pack_size || '—'}
              </div>
              <div className={css(styles.mutedText)}>
                {`MFR SKU ${(zoomedCandidate.mfr_product || {}).manufacturer_sku || '—'}`}
              </div>
              <div className={css(styles.zoomReasoningRow)}>
                <ChannelBadges channels={zoomedCandidate.channels} />
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  container: { minHeight: '100vh', backgroundColor: backgroundGrey },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
    borderLeft: `4px solid ${grape}`,
    paddingLeft: 12,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 },
  headerMeta: { color: coolGrey, fontSize: 12 },
  scopeBadge: {
    color: softAmber,
    backgroundColor: softAmberBackground,
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 6,
  },
  scopeWarning: {
    color: softAmber,
    marginBottom: 6,
  },
  title: { color: blackberry, fontWeight: 700, fontSize: 18 },
  runningIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 12,
    color: softAmber,
    fontWeight: 600,
    gap: 4,
  },
  progressTrack: {
    width: 140,
    height: 6,
    borderRadius: 3,
    backgroundColor: greyStroke,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: grape,
    transition: 'width 0.4s ease',
  },
  dispatchBtn: {
    backgroundColor: grape,
    borderColor: grape,
    color: white,
    fontWeight: 600,
    fontSize: 12,
    padding: '4px 12px',
  },
  newRunBtn: { fontSize: 12, padding: '4px 12px', fontWeight: 600 },
  limitInput: {
    width: 80,
    height: 30,
    fontSize: 12,
    padding: '4px 8px',
  },
  totalBadge: {
    backgroundColor: softPurpleLight,
    color: blackberry,
    fontSize: 13,
    padding: '6px 10px',
  },
  reviewWrapper: { maxWidth: 1400, margin: '0 auto' },
  navRow: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    position: 'relative',
  },
  gmvSortSlot: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  navBtn: {
    minWidth: 130,
    backgroundColor: blackberry,
    borderColor: blackberry,
    color: white,
    fontWeight: 600,
  },
  positionText: {
    color: coolGrey,
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  jumpInput: {
    width: 64,
    height: 28,
    fontSize: 13,
    fontWeight: 600,
    padding: '2px 6px',
    textAlign: 'center',
  },
  statusBanner: {
    marginTop: 12,
    padding: '8px 14px',
    borderRadius: 8,
    backgroundColor: softPurpleLight,
    color: softPurple,
    fontWeight: 700,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontSize: 12,
  },
  productCard: {
    position: 'relative',
    borderRadius: 12,
    backgroundColor: white,
    border: `1px solid ${greyStroke}`,
    padding: 16,
    paddingRight: 46, // reserves room for the icon rail
    height: '100%',
  },
  sideLabel: {
    display: 'flex',
    alignItems: 'center',
    height: 30,
    color: coolGrey,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontWeight: 700,
    marginBottom: 14,
  },
  productImageWrap: {
    width: 220,
    height: 220,
    margin: '0 auto 14px',
  },
  productImageLarge: {
    width: 220,
    height: 220,
    objectFit: 'contain',
    borderRadius: 10,
    border: 'none',
    backgroundColor: white,
  },
  productImagePlaceholderLarge: {
    width: 220,
    height: 220,
    borderRadius: 10,
    backgroundColor: softPurpleLight,
  },
  productName: { color: blackberry, fontWeight: 600, fontSize: 17, marginBottom: 4 },
  mutedText: { color: coolGrey, fontSize: 15 },
  // Pins to the bottom regardless of candName's line count.
  candBottomBlock: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  candSkuReasonRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  candHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 30,
    marginBottom: 14,
  },
  candHeaderText: { color: blackberry, fontWeight: 600, fontSize: 13 },
  candScrollArea: {
    maxHeight: 640,
    overflowY: 'auto',
    paddingRight: 4,
  },
  candGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
  },
  candCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${greyStroke}`,
    borderRadius: 12,
    padding: 12,
    paddingRight: 42, // reserves room for the icon rail
    backgroundColor: white,
    cursor: 'pointer',
    ':hover': { borderColor: grape, boxShadow: `0 2px 8px ${shadowLight}` },
  },
  candCardSelected: {
    borderColor: grape,
    backgroundColor: softPurpleLight,
    boxShadow: `0 0 0 2px ${softPurpleLight}`,
  },
  candCardMatched: {
    borderColor: positiveGreen,
    boxShadow: `0 0 0 2px ${positiveGreen}`,
  },
  matchedBadge: {
    position: 'absolute',
    top: 34,
    left: '50%',
    transform: 'translateX(-50%)',
    color: white,
    backgroundColor: softGreenDark,
    fontWeight: 700,
    fontSize: 10,
    padding: '2px 8px',
    borderRadius: 8,
    zIndex: 1,
  },
  rankBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    color: white,
    backgroundColor: blackberry,
    fontWeight: 700,
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 8,
    zIndex: 1,
  },
  candTopRightRow: {
    position: 'absolute',
    top: 10,
    right: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    zIndex: 1,
  },
  candStatusPill: {
    color: coolGrey,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.2,
  },
  dataSourceLabel: {
    display: 'inline-block', // needed for maxWidth/textOverflow to apply on an inline span
    color: coolGrey,
    backgroundColor: backgroundGrey,
    border: `1px solid ${greyStroke}`,
    fontSize: 9,
    fontWeight: 600,
    padding: '3px 7px',
    borderRadius: 8,
    whiteSpace: 'nowrap',
    maxWidth: 110, // long DataSourceOrigin names must not push into the rank/Matched badges
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tierPillFloating: {
    color: white,
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 8,
  },
  candImageWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 18,
    marginBottom: 10,
  },
  candImage: {
    width: 96,
    height: 96,
    objectFit: 'contain',
    borderRadius: 8,
    border: 'none',
    backgroundColor: white,
  },
  candImagePlaceholder: {
    width: 96,
    height: 96,
    borderRadius: 8,
    backgroundColor: softPurpleLight,
  },
  candName: {
    color: blackberry,
    fontWeight: 600,
    fontSize: 15,
    marginBottom: 2,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  candIconRail: {
    position: 'absolute',
    top: '50%',
    right: 8,
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    zIndex: 1,
  },
  productIconRail: {
    top: 154, // aligns with productImageWrap's vertical center
  },
  candIconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: white,
    boxShadow: `0 1px 4px ${shadowLight}`,
    color: coolGrey,
    fontSize: 15,
    cursor: 'pointer',
    ':hover': { color: grape, backgroundColor: softPurpleLight },
  },
  candIconButtonDisabled: {
    color: greyStroke,
    cursor: 'not-allowed',
    ':hover': { color: greyStroke, backgroundColor: 'transparent' },
  },
  zoomModalDialog: {
    width: 'fit-content', // sizes to content instead of react-bootstrap's fixed lg/xl presets
    maxWidth: '95vw',
  },
  zoomModalBody: {
    position: 'relative',
    display: 'flex',
    gap: 24,
    alignItems: 'center', // aligns details column to the image's vertical level
    justifyContent: 'center', // centers the image+details row as a group
    paddingTop: 8,
    paddingBottom: 8,
  },
  zoomImage: {
    width: 440,
    height: 440,
    maxWidth: '100%',
    objectFit: 'contain',
    borderRadius: 8,
    backgroundColor: white,
    flexShrink: 0,
  },
  zoomImagePlaceholder: {
    width: 440,
    height: 440,
    maxWidth: '100%',
    borderRadius: 8,
    backgroundColor: softPurpleLight,
    flexShrink: 0,
  },
  // Floats in zoomModalBody -- no Modal.Header, which would throw off centering.
  zoomCloseButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: white,
    boxShadow: `0 1px 4px ${shadowLight}`,
    color: coolGrey,
    fontSize: 18,
    cursor: 'pointer',
    zIndex: 2,
    ':hover': { color: blackberry },
  },
  zoomPdpButton: {
    right: 52, // sits left of zoomCloseButton
  },
  zoomDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    width: 320, // fixed so a long name wraps instead of stretching the modal
    maxWidth: '100%',
  },
  zoomName: {
    display: 'block',
    WebkitLineClamp: 'unset',
    overflow: 'visible',
    marginBottom: 4,
    fontSize: 18, // reads as this panel's heading now that Modal.Title is gone
    fontWeight: 700,
  },
  zoomDataSourceLabel: {
    alignSelf: 'flex-start',
    fontSize: 13,
    maxWidth: 280, // wider cap than the card chip -- zoomDetails has more room
  },
  zoomTierPill: {
    alignSelf: 'flex-start',
    marginBottom: 4,
    fontSize: 15,
  },
  zoomReasoningRow: {
    marginTop: 6,
  },
  actionsRow: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: backgroundGrey,
    marginTop: 12,
    paddingTop: 12,
    paddingBottom: 12,
    borderBottom: `1px solid ${greyStroke}`,
  },
  channelBadgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 0,
  },
  channelBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: softPurple,
    backgroundColor: softPurpleLight,
    padding: '2px 7px',
    borderRadius: 6,
  },
  channelBadgeAmbiguous: {
    color: softAmber,
    backgroundColor: softAmberBackground,
  },
  approveBtn: {
    backgroundColor: positiveGreen,
    borderColor: positiveGreen,
    color: white,
    fontWeight: 600,
    ':hover': { backgroundColor: softGreenDark, borderColor: softGreenDark },
  },
  neutralBtn: {
    fontWeight: 600,
    backgroundColor: white,
    borderColor: grape,
    color: blackberry,
    ':hover': { backgroundColor: softPurpleLight },
  },
  rejectBtn: {
    backgroundColor: modernSalmonBackground,
    borderColor: modernSalmonBackground,
    color: modernSalmon,
    fontWeight: 600,
    ':hover': { backgroundColor: modernSalmon, color: white },
  },
  hintText: {
    color: coolGrey,
    fontSize: 12,
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
  toggleWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  toggleTrack: {
    position: 'relative',
    width: 36,
    height: 20,
    borderRadius: 10,
    backgroundColor: greyStroke,
    flexShrink: 0,
  },
  toggleTrackOn: { backgroundColor: grape },
  toggleThumb: {
    position: 'absolute',
    top: 2,
    left: 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: white,
  },
  toggleThumbOn: { left: 18 },
  toggleText: { fontSize: 13, fontWeight: 500, color: blackberry },
});

BrandSkuMapperV2ReviewView.propTypes = {
  population: PropTypes.oneOf(Object.values(BrandSkuMapperV2Population)),
};

BrandSkuMapperV2ReviewView.defaultProps = {
  population: BrandSkuMapperV2Population.GMV,
};

export default BrandSkuMapperV2ReviewView;
